import React, { useState, useRef } from "react";
import { Box, Text } from "@origin-digital/ods-core";
import { Skeleton } from "@origin-digital/ods-lab";
import { logger } from "@origin-digital/reporting-client";
import { ThumbBtn } from "./AIAnswer.styles";
import { content } from "./AIAnswer.content";
import { IconThumbDown, IconThumbUp } from "@origin-digital/ods-icons";
import { postSearchAiFeedback } from "../../services/searchAi";
import { RatingEnum } from "../../contract";

type FeedbackVote = "up" | "down";

interface AIAnswerFeedbackProps {
  threadId: string | null;
  messageId: string | null;
}

export const AIAnswerFeedback: React.FC<AIAnswerFeedbackProps> = ({
  threadId,
  messageId,
}) => {
  const [vote, setVote] = useState<FeedbackVote | null>(null);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const submittingRef = useRef<boolean>(false);

  const handleVote = async (selected: FeedbackVote) => {
    if (submittingRef.current || threadId === null) {
      return;
    }

    submittingRef.current = true;
    setHasError(false);
    setVote(selected);
    setSubmitting(true);

    try {
      await postSearchAiFeedback({
        threadId,
        messageId: messageId ?? undefined,
        rating: selected === "up" ? RatingEnum.POSITIVE : RatingEnum.NEGATIVE,
      });
      setSubmitting(false);
      setSubmitted(true);
    } catch (error) {
      void logger.error(
        `[AIAnswerFeedback] feedback failed — ${String(error)}`
      );
      setHasError(true);
      setVote(null);
      setSubmitting(false);
      submittingRef.current = false;
    }
  };

  if (submitting || submitted) {
    return (
      <Box
        marginTop="xlarge"
        backgroundColor="grey50"
        padding="medium"
        borderRadius="small"
      >
        {submitting ? (
          <Skeleton variant="text" backgroundColor="light">
            <Text variant="body-small" weight="regular">
              {content.thanksFeedback}
            </Text>
          </Skeleton>
        ) : (
          <Text variant="body-small" tone="neutralDark" weight="regular">
            {content.thanksFeedback}
          </Text>
        )}
      </Box>
    );
  }

  return (
    <Box marginTop="xlarge">
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        data-id="ai-answer-feedback"
      >
        <Text variant="body-small" tone="neutralLight" weight="regular">
          {content.feedbackLabel}
        </Text>
        <Box display="flex" alignItems="center" gap="xxxsmall">
          <ThumbBtn
            $active={vote === "up"}
            onClick={() => void handleVote("up")}
            aria-label={content.thumbsUpAriaLabel}
            data-id="ai-answer-thumbs-up"
            disabled={threadId === null}
          >
            <IconThumbUp
              size="small"
              color={vote === "up" ? "neutralDark" : "neutralLight"}
              isDecorative
            />
          </ThumbBtn>
          <ThumbBtn
            $active={vote === "down"}
            onClick={() => void handleVote("down")}
            aria-label={content.thumbsDownAriaLabel}
            data-id="ai-answer-thumbs-down"
            disabled={threadId === null}
          >
            <IconThumbDown
              size="small"
              color={vote === "down" ? "neutralDark" : "neutralLight"}
              isDecorative
            />
          </ThumbBtn>
        </Box>
      </Box>
      {hasError && (
        <Text
          variant="body-small"
          tone="critical"
          weight="regular"
          data-id="ai-answer-feedback-error"
        >
          {content.feedbackError}
        </Text>
      )}
    </Box>
  );
};
